//  SubmissionsList  -  Shows  all  user's  submissions  in  cards
//  Displays  status,  amount,  and  allows  navigation  to  details
// I've commented out the Create button until we an refine workflow and changed the instructions for the ITB
import  React,  {  useState,  useEffect,  useMemo  }  from  'react';
import  {
    Box,
    Card,
    CardContent,
    CardActions,
    Typography,
    Button,
    Grid,
    Chip,
    CircularProgress,
    Alert,
    Divider,
    TextField,
    MenuItem,
}  from  '@mui/material';
import  {
    Visibility  as  ViewIcon,
    Delete  as  DeleteIcon,
    Send  as  SendIcon,
    Warning  as  WarningIcon,
}  from  '@mui/icons-material';
import  CreateSubmissionButton  from  '../common/CreateSubmissionButton';
import  {  formatSubmissionPeriod  }  from  '../../utils/dateHelpers';
import  {  useToast  }  from  '../../contexts/ToastContext';
import  {  useNavigate  }  from  'react-router-dom';
import  {  submissionsAPI  }  from  '../../services/api';
import  {  getStatusColor,  getStatusLabel  }  from  '../../utils/statusHelpers';
import  type  {  Submission  }  from  '../../types';
import  dayjs  from  'dayjs';

const  SubmissionsList:  React.FC  =  ()  =>  {
    const  [submissions,  setSubmissions]  =  useState<Submission[]>([]);
    const  [loading,  setLoading]  =  useState(true);
    const  [error,  setError]  =  useState<string  |  null>(null);
    const  [statusFilter,  setStatusFilter]  =  useState<string>('AllExceptApproved');  //  Changed  default
    const  navigate  =  useNavigate();
    const  {  showSuccess,  showError  }  =  useToast();

    useEffect(()  =>  {
        fetchSubmissions();
    },  []);

    const  fetchSubmissions  =  async  ()  =>  {
        try  {
            setLoading(true);
            setError(null);
            const  data  =  await  submissionsAPI.getMySubmissions();
            setSubmissions(data);
        }  catch  (err:  any)  {
            setError(err.response?.data?.message  ||  'Failed  to  load  submissions');
            console.error('Error  fetching  submissions:',  err);
        }  finally  {
            setLoading(false);
        }
    };

    const  handleDelete  =  async  (id:  string)  =>  {
        if  (!window.confirm('Are  you  sure  you  want  to  delete  this  submission?'))  {
            return;
        }

        try  {
            await  submissionsAPI.deleteSubmission(id);
            setSubmissions(submissions.filter(s  =>  s.submissionId  !==  id));
            showSuccess('Submission  deleted  successfully');
        }  catch  (err:  any)  {
            showError(err.response?.data?.message  ||  'Failed  to  delete  submission');
        }
    };

    //  ========================================
    //  FILTERING
    //  ========================================

    const  filteredSubmissions  =  useMemo(()  =>  {
        if  (statusFilter  ===  'All')  {
            return  submissions;
        }
        if  (statusFilter  ===  'AllExceptApproved')  {
            return  submissions.filter(s  =>  s.status  !==  'Approved');
        }
        return  submissions.filter(s  =>  s.status  ===  statusFilter);
    },  [submissions,  statusFilter]);

    //  ========================================
    //  RENDER
    //  ========================================

    if  (loading)  {
        return  (
            <Box  display="flex"  justifyContent="center"  alignItems="center"  minHeight="400px">
                <CircularProgress  />
            </Box>
        );
    }

    if  (error)  {
        return  <Alert  severity="error">{error}</Alert>;
    }

    return  (
        <Box>
            {/*  Welcome  Section  +  Quick  Start  Guide  */}
            <Grid  container  spacing={3}  sx={{  mb:  3  }}>
                {/*  Welcome  Section  -  2/3  width  */}
                <Grid  item  xs={12}  md={8}>
                    <Card>
                        <CardContent>
                            <Typography  variant="h4"  gutterBottom>
                                Welcome  to  Expense  Reimbursement
                            </Typography>
                            <Typography  variant="body1"  color="text.secondary"  paragraph>
                                Submit  your  business  expenses  for  reimbursement  quickly  and  easily.  
                                Choose  from  available  programs below.  
                                Track  your  submissions  and  receive  updates  on  approval  status.
                            </Typography>
                            <Typography  variant="body2"  color="text.secondary"  paragraph>
                                All  submissions  require  receipts  and  manager  approval.  Please  ensure  
                                your  expenses  comply  with  company  policy  before  submitting.
                            </Typography>
                          {/*  <Box  sx={{  mt:  3  }}>
                          //      <CreateSubmissionButton  />
                          //  </Box>*/}
                        </CardContent>
                    </Card>
                </Grid>

                {/*  Quick  Start  Guide  -  1/3  width  */}
                <Grid  item  xs={12}  md={4}>
                    <Card  sx={{  bgcolor:  'primary.main',  color:  'white',  height:  '100%'  }}>
                        <CardContent>
                            <Typography  variant="h6"  gutterBottom>
                                Quick  Start  Guide
                            </Typography>
                            <Typography  variant="body2"  paragraph>
                                1.  Look at the list of available Programs below and click "Edit"
                            </Typography>
                            <Typography  variant="body2"  paragraph>
                                2.  Add Receipts by clicking "Add Receipt" and fill in required fields
                            </Typography>
                            <Typography  variant="body2"  paragraph>
                                3.  Click "Submit for Approval" and type in your name to confirm, click submit
                            </Typography>
                            <Typography  variant="body2">
                                4.  Await manager  approval or instructions from your manager
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/*  Page  Title  */}
            <Typography  variant="h5"  sx={{  mb:  3  }}>
                My  Submissions
            </Typography>

            {/*  Filter  Section  */}
            <Card  sx={{  mb:  3  }}>
                <CardContent>
                    <Grid  container  spacing={2}  alignItems="center">
                        <Grid  item  xs={12}  md={4}>
                            <TextField
                                select
                                fullWidth
                                label="Filter  by  Status"
                                value={statusFilter}
                                onChange={(e)  =>  setStatusFilter(e.target.value)}
                            >
                                <MenuItem  value="AllExceptApproved">All  Active  Submissions</MenuItem>
                                <MenuItem  value="All">All  Submissions</MenuItem>
                                <MenuItem  value="Draft">Drafts</MenuItem>
                                <MenuItem  value="Pending Manager Approval">Pending Manager Approval</MenuItem>
                                <MenuItem  value="Pending  HR  Approval">Pending  HR</MenuItem>
                                <MenuItem  value="Approved">Approved</MenuItem>
                                <MenuItem  value="Rejected">Rejected</MenuItem>
                            </TextField>
                        </Grid>
                        <Grid  item  xs={12}  md={8}>
                            <Typography  variant="body2"  color="text.secondary"  align="right">
                                Showing  <strong>{filteredSubmissions.length}</strong>  of  <strong>{submissions.length}</strong>  submissions
                            </Typography>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            {filteredSubmissions.length  ===  0  ?  (
                <Card>
                    <CardContent>
                        <Typography  variant="body1"  color="text.secondary"  align="center">
                            {submissions.length  ===  0  
                                ?  "No  submissions  yet.  Click  'Create  New  Submission'  to  get  started!"
                                :  "No  submissions  match  the  selected  filter."}
                        </Typography>
                    </CardContent>
                </Card>
            )  :  (
                <Grid  container  spacing={3}>
                    {filteredSubmissions.map((submission)  =>  (
                        <Grid  item  xs={12}  md={6}  key={submission.submissionId}>
                            <Card  
                                sx={{  
                                    border:  submission.isRejected  ?  '2px  solid'  :  undefined,
                                    borderColor:  submission.isRejected  ?  'error.main'  :  undefined
                                }}
                            >
                                <CardContent>
                                    <Box  display="flex"  justifyContent="space-between"  alignItems="start"  mb={2}>
                                        <Box>
                                            <Typography  variant="h6"  component="div">
                                                {submission.displayName}
                                            </Typography>
                                            {submission.programIsArchived  &&  (
                                                <Chip  
                                                    label="Program  Archived"  
                                                    size="small"  
                                                    color="default"
                                                    sx={{  mt:  0.5,  mr:  0.5  }}
                                                />
                                            )}
                                            {/* Resubmitted badge temporarily disabled */}
{/* {submission.wasResubmitted && (
  <Chip
    label="Resubmitted"
    size="small"
    color="info"
  />
)} */}
                                        </Box>
                                        <Chip
                                            label={getStatusLabel(submission.status)}
                                            color={getStatusColor(submission.status)}
                                            size="small"
                                        />
                                    </Box>

                                    {/*  Show  rejection  alert  if  rejected  */}
                                    {submission.isRejected  &&  submission.rejectionReason  &&  (
                                        <Alert  
                                            severity="error"  
                                            icon={<WarningIcon  />}
                                            sx={{  mb:  2  }}
                                        >
                                            <Typography  variant="body2">
                                                <strong>Rejected:</strong>  {submission.rejectionReason}
                                            </Typography>
                                            {submission.canEdit  &&  (
                                                <Typography  variant="caption"  display="block"  sx={{  mt:  0.5  }}>
                                                    You  can  edit  and  resubmit  this  submission.
                                                </Typography>
                                            )}
                                        </Alert>
                                    )}

                                    <Typography  variant="body2"  color="text.secondary"  gutterBottom>
                                        Period:  {formatSubmissionPeriod(submission)}
                                    </Typography>

                                    <Divider  sx={{  my:  1.5  }}  />

                                    <Box  display="flex"  justifyContent="space-between"  alignItems="center">
                                        <Box>
                                            <Typography  variant="h5"  color="primary.main">
                                                ${submission.totalAmount.toFixed(2)}
                                            </Typography>
                                            {submission.amountToBePaid  &&  submission.amountToBePaid  <  submission.totalAmount  &&  (
                                                <Typography  variant="caption"  color="warning.main"  display="block">
                                                    Payment  capped  at  ${submission.amountToBePaid.toFixed(2)}
                                                </Typography>
                                            )}
                                            <Typography  variant="caption"  color="text.secondary">
                                                {submission.receiptCount}  receipt{submission.receiptCount  !==  1  ?  's'  :  ''}
                                            </Typography>
                                            
                                            {/*  Allowance  Information  */}
                                            {submission.allowanceInfo  &&  submission.allowanceInfo.hasAllowance  &&  (
                                                <Box  sx={{  mt:  1.5,  p:  1,  bgcolor:  '#e3f2fd',  borderRadius:  1  }}>
                                                    <Typography  variant="caption"  color="text.secondary"  display="block">
                                                        Allowance:  ${submission.allowanceInfo.programAllowance?.toFixed(2)}
                                                    </Typography>
                                                    <Typography variant="caption" display="block">
    Used: ${submission.allowanceInfo.usedAmount.toFixed(2)} | 
    Remaining: <span style={{ color: Math.max(0, submission.allowanceInfo.remainingAmount) >= 0 ? '#2e7d32' : '#d32f2f' }}>
        ${Math.max(0, submission.allowanceInfo.remainingAmount).toFixed(2)}
    </span>
</Typography>
                                                </Box>
                                            )}
                                        </Box>

                                        {submission.submittedDate  &&  (
                                            <Typography  variant="caption"  color="text.secondary">
                                                Submitted  {dayjs(submission.submittedDate).format('MMM  D,  YYYY')}
                                            </Typography>
                                        )}
                                    </Box>

                                    {submission.submissionNotes  &&  (
                                        <Typography  variant="body2"  color="text.secondary"  sx={{  mt:  1.5  }}>
                                            Notes:  {submission.submissionNotes}
                                        </Typography>
                                    )}
                                </CardContent>

                                <CardActions  sx={{  justifyContent:  'flex-end',  px:  2,  pb:  2  }}>
                                    {submission.canEdit  &&  (
                                        <Button
                                            size="small"
                                            color="error"
                                            startIcon={<DeleteIcon  />}
                                            onClick={()  =>  handleDelete(submission.submissionId)}
                                        >
                                            Delete
                                        </Button>
                                    )}
                                    <Button
                                        size="small"
                                        variant="contained"
                                        startIcon={<ViewIcon  />}
                                        onClick={()  =>  navigate(`/submissions/${submission.submissionId}`)}
                                    >
                                        {submission.canEdit  ?  'Edit'  :  'View  Details'}
                                    </Button>
                                </CardActions>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            )}
        </Box>
    );
};

export  default  SubmissionsList;